import empty from "is-empty";
import { toApiCollType } from "src/utils/converting";
import { ROLE } from "src/redux/TopBar/constants";
import { createSelector } from "@reduxjs/toolkit";
import { InventorySelectors } from "src/redux/Inventory/selectors";

const FormattedListSettings = (listSettings) => {
    //with format for updating server
    return listSettings.map((rowData) => {
        const listSetting = {
            ...rowData,
            collateralType: toApiCollType(rowData.collType, rowData.collCcy),
            ksdAccount: rowData.ksdAccount?.accountNo,
            ksfcAccount: rowData.ksfcAccount?.accountNo,
        };
        delete listSetting.collType;
        delete listSetting.collCcy;
        return listSetting;
    });
};

const FormattedFundSettings = (fundSettings) => {
    return fundSettings.map((rowData) => {
        const fundSetting = {
            ...rowData,
            ksdAccount: rowData.ksdAccount?.accountNo,
            ksfcAccount: rowData.ksfcAccount?.accountNo,
        };

        return fundSetting;
    });
};

const mapBlockedSecuritiesRow = (entry, index) => {
    let row = {
        id: entry.id, // this is the Security ID, globally unique
        Name: entry.name,
        BbgTicker: entry.bbgCode,
        Isin: entry.isin,
        LastUpdated: entry.lastUpdated,
        User: entry.lastUser,
        isNew: entry.isNew,
    };

    return row;
};

export const SettingsSelectors = {
    AOSettings: (state) => state.Settings.aoSettings,
    KsdAccountSettings: (state) => state.Settings.ksdAccountSettings,
    KsfcAccountSettings: (state) => state.Settings.ksfcAccountSettings,
    AvailabilitySettings: (state) => state.Settings.availabilitySettings,

    // only returns the active blocked securities, without any market data
    LenderBlockedSecuritiesSettings: (state) => {
        return state.Settings.blockedSecurities ?? [];
    },

    // for use with the settings screen with current & pending blocked securities list
    LenderPendingBlockedSecuritiesWithMarketData: (state) => {
        return (state.Settings.pendingBlockedSecurities ?? []).map(
            mapBlockedSecuritiesRow
        );
    },

    EmailsSettings: (state) => state.Settings.emailSettings,
    ConfirmationFileFields: (state) => state.Settings.confirmationFileFields,
    DynamicAndClientPoolList: (state) => state.Settings.dynamicAndClientPoolList,
    ListSettings: (state) => state.Settings.listSettings,
    PendingListSettings: (state) => state.Settings.pendingListSettings,
    /** What the list-settings dialog's table shows: the confirmed lists plus the
     *  drafts added in this dialog, so Add displays the new list immediately
     *  while nothing unconfirmed enters the collection the rest of the app reads.
     *  See docs/plans/list-settings-pending-drafts.md. */
    ListSettingsForDialog: (state) => [
        ...state.Settings.listSettings,
        ...state.Settings.pendingListSettings,
    ],
    FundSettings: (state) => state.Settings.fundSettings,
    DisplayListErrors: (state) => state.Settings.displayListErrors,
    DisplayFundErrors: (state) => state.Settings.displayFundErrors,
    Settings: (state, role) => {
        const {
            aoSettings,
            emailSettings,
            availabilitySettings,
            ksdAccountSettings,
            ksfcAccountSettings,
            generalAccountSettings,
            fundSettings,
            confirmationFileFields,
        } = state.Settings;
        const autoOfferSettings = { ...aoSettings };
        const listSettings = FormattedListSettings(state.Settings.listSettings);

        const result = {
            role: role === ROLE.LENDER ? "LENDER" : "BORROWER",
            autoOfferSettings,
            availabilitySettings,
            listSettings,
            fundSettings,
            defaultEmails:
                role === ROLE.BORROWER
                    ? emailSettings.borrowerEmails
                    : emailSettings.lenderEmails,
            generalAccountSettings,
            confirmationFileFields,
        };

        if (!empty(ksdAccountSettings))
            result.ksdAccountSettings = ksdAccountSettings;
        if (!empty(ksfcAccountSettings))
            result.ksfcAccountSettings = ksfcAccountSettings;

        return result;
    },

    LenderListSettings: (state) => {
        // Drafts live outside listSettings, so the payload unions both: an edited
        // confirmed list and a newly added draft are both upserts.
        const listSettings = FormattedListSettings([
            ...state.Settings.listSettings,
            ...state.Settings.pendingListSettings,
        ]);
        return {
            upsert: listSettings.filter((l) => l.isNew || l.hasUpdates),
            delete: state.Settings.deletedListSettings,
        };
    },
    BorrowerFundSettings: (state) => {
        const fundSettings = FormattedFundSettings(state.Settings.fundSettings);
        return {
            upsert: fundSettings.filter((l) => l.isNew),
            delete: state.Settings.deletedFundSettings,
        };
    },
    MyDynamicList: (state, listName) =>
        state.Settings.dynamicAndClientPoolList.filter((d) => d.parentName === listName),
    MatchedListForNewOffer: (state, listName) => {
        return state.Settings.listSettings
            .concat(state.Settings.dynamicAndClientPoolList)
            .find((list) => list.name === listName);
    },
    ListSettingsWithOrder: (state) => {
        const { listSettings } = state.Settings;
        return (listSettings || []).map((l) => l.name);
    },
    SelectableListNamesForBrokerCpty: (state, bbgCode) => {
        const todayInventory = InventorySelectors.GetTodayInventory(state);

        const resultSet = new Set(
            Object.values(todayInventory)
                .flat()
                .filter((entry) => entry.truQuantity > 0)
                .map((entry) => entry.listName + "_" + entry.security.bbgCode)
        );

        let result = [];
        const { listSettings, dynamicAndClientPoolList } = state.Settings;
        for (let i = 0; i < listSettings.length; i++) {
            const dynList = SettingsSelectors.MyDynamicList(
                state,
                listSettings[i].name
            );
            if (dynList.length > 0) {
                result = result.concat(
                    dynList
                        .filter((d) => resultSet.has(d.name + "_" + bbgCode))
                        .map((d) => d.name)
                );
            }

            // always add parent list
            result.push(listSettings[i].name);
        }
        // add rest of dynamic lists (which don't have parent, to test)
        result = result.concat(
            dynamicAndClientPoolList
                .filter(
                    (d) =>
                        !result.includes(d.name) &&
                        resultSet.has(d.name + "_" + bbgCode)
                )
                .map((d) => d.name)
        );
        return result;
    },
    SelectableListNames: (state) => {
        let result = [];
        const { listSettings, dynamicAndClientPoolList } = state.Settings;
        for (let i = 0; i < listSettings.length; i++) {
            result.push(listSettings[i].name);

            const dynList = SettingsSelectors.MyDynamicList(
                state,
                listSettings[i].name
            );
            if (dynList.length > 0) {
                result = result.concat(dynList.map((d) => d.name));
            }
        }
        // add rest of dynamic lists (which don't have parent, to test)
        result = result.concat(
            dynamicAndClientPoolList
                .filter((d) => !result.includes(d.name))
                .map((d) => d.name)
        );
        return result;
    },
    SelectableFundNames: (state) => {
        return ["None"].concat(
            state.Settings.fundSettings.map((fund) => fund.fundName)
        );
    },
    UserSettings: (state) => state.Settings?.userSettings,
    GetPreferredCcy: (state) => state.Settings?.userSettings?.preferredCcy,
    UserEmailOptIns: (state) => state.Settings?.userSettings?.emailOptIns,
    PeerCptySettings: (state) => state.Settings?.peerCptySettings || [],
    FeatureFlags: (state) => state.Settings?.featureFlags ?? {},
    SelectedPeerCptySetting: (state) =>
        state.Settings?.selectedPeerCptySetting ?? {},
    NewPeerCptySetting: (state) => state.Settings?.newPeerCptySettings ?? [],
    GeneralAccountSettings: (state) =>
        state.Settings?.generalAccountSettings ?? [],
};

// Derived selectors (reduce calculation)
export const SelectPeerCptySetting = createSelector(
    [SettingsSelectors.PeerCptySettings, (state, cptyId) => cptyId],
    (settings, cptyId) => settings.find((s) => s.cptyCode === cptyId)
);

export const UiPeerCptySettings = createSelector(
    SettingsSelectors.PeerCptySettings,
    (settings) =>
        settings
            .sort((a, b) => a.cptyCode.localeCompare(b.cptyCode))
            .map((c) => ({ ...c, originValue: { ...c } }))
);
